import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Avatar,
  alpha,
} from '@mui/material';
import { API_BASE_URL } from '@/config';
import { useAudio } from '@/context/AudioProvider';
import { PLACEHOLDER_ALBUM, PLACEHOLDER_ARTIST } from '@/services/mediaService';

interface Artist {
  id: string;
  name: string;
  image_url?: string;
  slug?: string;
}

interface Album {
  id: string;
  title: string;
  artist_name?: string;
  artwork_url?: string;
  release_year?: number;
  artist_id?: string;
  slug?: string;
}

interface Genre {
  id: string;
  name: string;
}

const GenrePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { playTrack } = useAudio();
  
  const [activeTab, setActiveTab] = useState(0); // 0 = Artists, 1 = Albums
  const [artists, setArtists] = useState<Artist[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [allGenres, setAllGenres] = useState<Genre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to ensure URLs are complete
  const SUPABASE_PUBLIC_BASE_URL = "https://kkzpbiopvklopgxzkgrh.supabase.co/storage/v1/object/public";
  const getFullImageUrl = (path?: string | null): string | undefined => {
    if (!path) return undefined;
    // Don't double-wrap if already a full URL
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Assume it's a path relative to the Supabase bucket
    return `${SUPABASE_PUBLIC_BASE_URL}/${path}`;
  };

  // Convert slug back to genre name (handle special characters)
  const genreName = slug?.replace(/-/g, '/').replace(/and/g, '&') || '';
  
  // Find the matching genre from our list
  const currentGenre = allGenres.find(g => 
    g.id.toLowerCase() === genreName.toLowerCase()
  ) || { id: genreName, name: genreName };

  // Fetch all genres for the sidebar
  useEffect(() => {
    const fetchAllGenres = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/discover/genres`);
        if (response.ok) {
          const data = await response.json();
          // API returns { genres: ["Pop", "Rock", ...] }
          if (data.genres) {
            setAllGenres(data.genres.map((name: string) => ({
              id: name,
              name: name
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching all genres:', error);
      }
    };

    fetchAllGenres();
  }, []);

  // Fetch genre-specific data (artists & albums)
  useEffect(() => {
    const fetchGenreData = async () => {
      if (!genreName) return;
      
      setLoading(true);
      setError(null);

      try {
        // Fetch artists from genre endpoint (same as mobile)
        const artistsResponse = await fetch(`${API_BASE_URL}/discover/genre/${encodeURIComponent(genreName)}`);
        
        if (!artistsResponse.ok) {
          throw new Error('Failed to fetch genre artists');
        }

        const artistsData = await artistsResponse.json();
        setArtists(artistsData.artists || []);

        // Fetch ALL albums and filter client-side (same as mobile app)
        const albumsResponse = await fetch(`${API_BASE_URL}/public/album?q=a`);
        
        if (albumsResponse.ok) {
          const albumsData = await albumsResponse.json();
          const allAlbums = albumsData.albums || [];
          
          // Filter albums by genre name (case-insensitive)
          const filteredAlbums = allAlbums.filter((album: any) => {
            const genres = album.genre || [];
            return genres.some((genre: string) => 
              genre.toLowerCase() === genreName.toLowerCase()
            );
          });
          
          setAlbums(filteredAlbums);
        }
      } catch (err) {
        console.error('Error fetching genre data:', err);
        setError('Failed to load genre content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchGenreData();
  }, [genreName]);

  const handleArtistClick = (artist: Artist) => {
    if (artist.slug) {
      navigate(`/artist/${artist.slug}`);
    } else {
      navigate(`/artist/${artist.id}`);
    }
  };

  const handleAlbumClick = (album: Album) => {
    // Try to navigate with hierarchical route if we have artist info
    if (album.artist_id && album.slug) {
      navigate(`/artist/${album.artist_id}/album/${album.slug}`);
    } else {
      navigate(`/album/${album.id}`);
    }
  };

  const handleGenreChange = (genre: Genre) => {
    const genreSlug = genre.id.replace(/\//g, '-').replace(/&/g, 'and');
    navigate(`/genre/${encodeURIComponent(genreSlug)}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Sidebar with all genres */}
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            position: 'sticky', 
            top: 80,
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Genres
              </Typography>
              <List dense>
                {allGenres.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  allGenres.map((genre) => (
                    <ListItem key={genre.id} disablePadding>
                      <ListItemButton
                        selected={genre.id === currentGenre.id}
                        onClick={() => handleGenreChange(genre)}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            bgcolor: alpha('#E4FF1A', 0.15),
                            color: '#E4FF1A',
                            '&:hover': {
                              bgcolor: alpha('#E4FF1A', 0.25),
                            }
                          }
                        }}
                      >
                        <ListItemText primary={genre.name} />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Main content area */}
        <Grid item xs={12} md={9}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" gutterBottom>
              {currentGenre.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore artists and albums in {currentGenre.name}
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                },
                '& .Mui-selected': {
                  color: '#E4FF1A',
                }
              }}
            >
              <Tab label={`Artists (${artists.length})`} />
              <Tab label={`Albums (${albums.length})`} />
            </Tabs>
          </Box>

          {/* Content */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center" sx={{ py: 8 }}>
              {error}
            </Typography>
          ) : (
            <>
              {/* Artists Tab */}
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  {artists.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
                        No artists found in this genre
                      </Typography>
                    </Grid>
                  ) : (
                    artists.map((artist) => (
                      <Grid item xs={6} sm={4} md={3} key={artist.id}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                            },
                          }}
                          onClick={() => handleArtistClick(artist)}
                        >
                          <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                            <Avatar
                              src={getFullImageUrl(artist.image_url) || PLACEHOLDER_ARTIST}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                borderRadius: 0,
                              }}
                              variant="square"
                            />
                            {/* Gradient overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
                                p: 2,
                              }}
                            >
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 700,
                                  color: 'white',
                                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                                }}
                                noWrap
                              >
                                {artist.name}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              )}

              {/* Albums Tab */}
              {activeTab === 1 && (
                <Grid container spacing={2}>
                  {albums.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography align="center" color="text.secondary" sx={{ py: 8 }}>
                        No albums found in this genre
                      </Typography>
                    </Grid>
                  ) : (
                    albums.map((album) => (
                      <Grid item xs={6} sm={4} md={3} key={album.id}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                            },
                          }}
                          onClick={() => handleAlbumClick(album)}
                        >
                          <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                            <CardMedia
                              component="img"
                              image={getFullImageUrl(album.artwork_url) || PLACEHOLDER_ALBUM}
                              alt={album.title}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            {/* Gradient overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
                                p: 2,
                              }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 700,
                                  color: 'white',
                                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                  mb: 0.5,
                                }}
                                noWrap
                              >
                                {album.title}
                              </Typography>
                              {album.artist_name && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'rgba(255,255,255,0.8)',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                  }}
                                  noWrap
                                >
                                  {album.artist_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default GenrePage;

